// The drawn resource icons (R1764, R1768; ui-map-interface.md §8): the
// five commodities, the Data Core and the world clock, as line icons in a
// 24-unit box. They are the icons of mockup round 080 card 11 and of round
// 082, which draw the glyphs of identity.toml as shapes. Every HTML place
// that names a commodity by its sign draws them: the readout line, every
// cost (`state.ts costText`), the deposit lines, the auction, the INBOX
// and the arcade bank. A place that is not HTML (a `title`, a placeholder,
// a server string) keeps the font glyph of identity.toml.
//
// The SVG states no size: the rule `svg.ri` of styles.css draws it at 1em
// on the text line, and the rules of `#topbar` size it there (14 px on a
// desktop, 12 px on a telephone, as the mockup), so one markup string
// serves every form. The stroke is the stroke of the dock icons (R1740).
import { RESOURCES } from '../gen/balance';

/** The paths of each icon. A filled part states its own fill. */
const ICON = {
  // A hexagon: the alloy plate.
  alloy: '<path d="M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z"/>',
  // A four-point star: the biomass.
  biomass: '<path d="M12 3c.8 5 4 8.2 9 9-5 .8-8.2 4-9 9-.8-5-4-8.2-9-9 5-.8 8.2-4 9-9z"/>',
  // A diamond in a diamond: the volatiles.
  volatiles: '<path d="M12 3l9 9-9 9-9-9z"/><path d="M12 8.5l3.5 3.5-3.5 3.5-3.5-3.5z"/>',
  // The trefoil, filled: the isotopes.
  isotopes: '<path fill="currentColor" stroke="none" d="M12 9L12 3A9 9 0 0 1 19.794 7.5L14.598 10.5A3 3 0 0 0 12 9Z'
    + 'M14.598 13.5L19.794 16.5A9 9 0 0 1 12 21L12 15A3 3 0 0 0 14.598 13.5Z'
    + 'M9.402 13.5L4.206 16.5A9 9 0 0 1 4.206 7.5L9.402 10.5A3 3 0 0 0 9.402 13.5Z"/>'
    + '<circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/>',
  // The currency sign: a ring and four spurs.
  credits: '<circle cx="12" cy="12" r="5"/><path d="M5.5 5.5l2.5 2.5M18.5 5.5L16 8M5.5 18.5L8 16M18.5 18.5L16 16"/>',
  // A chip with its pins: the Data Core.
  core: '<rect x="7" y="7" width="10" height="10" rx="1"/>'
    + '<path d="M10 3.5V7M14 3.5V7M10 17v3.5M14 17v3.5M3.5 10H7M3.5 14H7M17 10h3.5M17 14h3.5"/>'
    + '<rect x="10" y="10" width="4" height="4" fill="currentColor" stroke="none"/>',
  // A dial with two hands: the world clock.
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
} as const;

/** The name of one drawn resource icon. */
export type ResIcon = keyof typeof ICON;

/** One drawn resource icon, hidden from a screen reader: the text beside
 *  it or the cell's title names the commodity. The class `ri-<name>`
 *  names the icon, so a test reads it without the paths. */
export function resIcon(name: ResIcon): string {
  return `<svg class="ri ri-${name}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"`
    + ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
    + `${ICON[name]}</svg>`;
}

/** Each commodity glyph of identity.toml, and the icon that draws it. */
const BY_GLYPH: ReadonlyMap<string, ResIcon> = new Map(RESOURCES.map((r) => [r.glyph, r.key]));
const glyphRe = (keys: readonly string[]) =>
  new RegExp(`[${RESOURCES.filter((r) => keys.includes(r.key)).map((r) => r.glyph).join('')}]`, 'gu');
const ALL_RE = glyphRe(RESOURCES.map((r) => r.key));
const MARK_RE = glyphRe(['isotopes', 'credits']);

/** An HTML string with each commodity glyph of identity.toml drawn as its
 *  icon. The sentences of the locale files (`PAY ¤{ransom}`) keep the
 *  glyph, and the place that writes one as HTML passes it through here.
 *  The input must be HTML already (escaped), never a raw user string. */
export function withResIcons(html: string): string {
  return html.replace(ALL_RE, (g) => resIcon(BY_GLYPH.get(g)!));
}

/** A kind mark of the INBOX or of the return briefing, with ¤ (the market)
 *  and ☢ (an expedition) drawn as their icons. ⬡ is a map cell there, not
 *  alloy, so it keeps its glyph, as every other kind mark does. */
export function withMarkIcons(html: string): string {
  return html.replace(MARK_RE, (g) => resIcon(BY_GLYPH.get(g)!));
}
